/** @jsxImportSource @emotion/react */
import { Flex } from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import FontFamily from "@tiptap/extension-font-family";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import * as HTMLEntities from "html-entities";
import juice from "juice";
import { nanoid } from "nanoid";
import { Fragment, Node, Slice } from "prosemirror-model";
import queryString from "query-string";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { isSafari } from "react-device-detect";
import { IoMdClose } from "react-icons/io";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import sanitizeHTML from "sanitize-html";
import { Instance, Props } from "tippy.js";
import { CharactersLenghtAtom } from "../atoms/CharactersLenghtAtom";
import EmailSelectorFamily, { EmailMode } from "../atoms/EmailAtom";
import { FullScreenAtom } from "../atoms/FullScreenAtom";
import { LinksAtom } from "../atoms/LinksAtom";
import { TestDriveBannerAtom } from "../atoms/TestDriveBannerAtom";
import { UserAtom } from "../atoms/UserAtom";
import { WordsLengthAtom } from "../atoms/WordsLengthAtom";
import { ZoomAtom } from "../atoms/ZoomAtom";
import { HorizontalRule } from "../components/NewEmail/horizontal-rule";
import { debounce } from "../hook/useDebounce";
import useEditorHistory from "../hook/useEditorHistory";
import useEmailEditorHistory, {
  SPLIT_SUBJECT_KEY,
  SUBJECT_KEY,
} from "../hook/useEmailEditorHistory";
import {
  addBoldInLinks,
  convertPagesMarginsToLineBreaksOnChrome,
  convertPagesMarginsToLineBreaksOnWebkit,
  useEmailEditorPasting,
} from "../hook/useEmailEditorPasting";
import useEmailEditorSubject from "../hook/useEmailEditorSubject";
import useLinkTooltips from "../hook/useLinkTooltips";
import useMediaQuery from "../hook/useMediaQuery";
import usePrevious from "../hook/usePrevious";
import useSetEmail from "../hook/useSetEmail";
import { BrowserAgent, useBrowserAgent } from "../utils/BrowserAgent";
import { INTERNAL_USER_FOR_AUTO_TEXT_STYLE, themes } from "../utils/constants";
import { fixCursorOnFrontOfTheLink } from "../utils/EmailEditorUtils";
import {
  getSelectedAnchorElement,
  SAFARI_DEFAULT_SELECTION_BACKGROUND_COLOR,
} from "../utils/LinkUtils";
import {
  localStorageGetItem,
  localStorageRemoveItem,
  localStorageSetItem,
} from "../utils/LocalStorage";
import {
  sanitizeHtmlOptions,
  sentToOneSanitizeHtmlOptions,
} from "../utils/SanitizeBerserkerEmail";
import EditorToolbar from "./NewCampaign/Table/Toolbar/EditorToolbar";
import { CustomStylingTiptap } from "./NewEmail/CustomStylingTiptap";
import { History } from "./NewEmail/history";
import { Strike } from "./NewEmail/strikethrough";
import {
  isNotTrimmedURL,
  isURL,
  selectionMarks,
  TiptapAutoLink,
} from "./NewEmail/TiptapAutoLink";
import { TiptapHeading } from "./NewEmail/TiptapHeading";
import TiptapMenu, {
  editorChainKeepAlign,
  TiptapMenuHandle,
  useUpdateBubbleMenu,
} from "./NewEmail/TiptapMenu";
import { Underline } from "./NewEmail/underline";
import ResizebleTextArea from "./ResizebleTextArea";
import SplitModePopover from "./SplitModePopover";

const REGEX_SEPARATOR = "|||";

const normalContainer = css`
  width: 100%;
  padding: 40px 180px 40px 180px;
  overflow: auto;
  @media (max-width: 1400px) {
    padding: 40px 100px 40px 100px;
  }

  @media (max-width: 850px) {
    padding: 40px 80px 40px 80px;
  }
`;

const fullScreeContainer = css`
  width: 100%;
`;

function linkify(
  text: string,
  linkColor: string,
  marks: string[],
  isAutoBoldLinkActive?: boolean
) {
  const matches = text.matchAll(
    /(h|H)ttps?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(((&amp;)|(\{\{Contact\.\w+\}\})|[-a-zA-Z0-9()@:%_\+.~#?&//=])*)/g
  );

  const content = [];
  let startPosition = 0;
  const markTypes = marks.map((mark) => ({ type: mark }));

  let linkMatchesFound = 0;
  let next: IteratorResult<RegExpMatchArray, any>;
  while (!(next = matches.next()).done) {
    const currentMatch = next.value;

    linkMatchesFound += 1;

    const url = currentMatch[0];
    const index = currentMatch.index;
    const preText = text.substr(startPosition, index);
    if (preText)
      content.push({ type: "text", text: preText, marks: markTypes });
    content.push({
      type: "mention",
      attrs: { id: url, label: url, colorLink: linkColor, tooltipId: nanoid() },
      marks: markTypes.concat([{ type: "underline" }]),
    });
    startPosition = index + url.length;
  }

  // This applies styling to text AFTER the found links
  const posText = text.substr(startPosition, text.length);

  if (posText) {
    // There's a bug where the TextExpander extension actually
    // goes into this as a link, so I need to now if I'm autobolding
    // the link ("bold" added as a permanent mark) and remove it
    // ONLY for the text after the links
    // - Palhari, 7/10/2023
    const markTypesForPostText = isAutoBoldLinkActive
      ? markTypes.filter((mt) => mt.type !== "bold")
      : markTypes;

    content.push({
      type: "text",
      text: posText,
      marks: markTypesForPostText,
    });
  }

  return content;
}

interface EmailBodyProps {
  fullScreen: boolean;
  isCampaign?: boolean;
  isResend?: boolean;
}

const EmailBody = styled.div<EmailBodyProps>`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;
  overflow-x: auto;
  ${(props) => (props.fullScreen ? fullScreeContainer : normalContainer)};
  ${(props) =>
    props.isCampaign &&
    css`
      border: 1px solid #dddddd;
      border-radius: 5px;
      padding: 20px !important;
      height: fit-content;
      min-height: 40vh;
      overflow: hidden;
    `};

  @media only screen and (max-width: 1000px) {
    width: 100%;
  }

  #subject {
    ::placeholder {
      color: #979797;
    }
  }

  ${(props) =>
    !props.isResend &&
    css`
      div[id^="tippy"] {
        z-index: 9998 !important;
      }
      #tippy-1 {
        z-index: 9998 !important;
      }
    `}
`;

const EmailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Body = styled.div<{ isCampaign: boolean; webkit: boolean }>`
  width: 100%;
  font-size: 14px;
  padding: 5px;
  color: #2f2f2f;
  height: 80%;
  line-height: 1.5em;
  border: none;
  border-radius: 0px;
  background-color: white;
  cursor: text;

  p {
    margin: 0px 0px 10px;
    white-space: pre-wrap;
  }

  .ProseMirror {
    padding-bottom: ${(props) => (props.isCampaign ? "0px" : "200px")};
  }

  /* Placeholder (at the top) */
  .ProseMirror p.is-editor-empty:first-of-type::before {
    content: attr(data-placeholder);
    float: left;
    pointer-events: none;
    height: 0;
  }

  .ProseMirror-hideselection a::selection {
    background: ${({ webkit }) =>
      webkit
        ? `${SAFARI_DEFAULT_SELECTION_BACKGROUND_COLOR} !important`
        : "default"};
  }

  .ProseMirror-hideselection,
  .ProseMirror-hideselection * {
    caret-color: ${({ webkit }) => (webkit ? "black !important" : "default")};
  }

  .ProseMirror a {
    user-select: ${({ webkit }) => (webkit ? "all !important" : "default")};
    -webkit-user-select: ${({ webkit }) =>
      webkit ? "all !important" : "default"};
  }
`;

const EditorToolbarWrapper = styled.div<{
  isTestDriverUser: boolean;
  isFullscreen: boolean;
  mode: EmailMode;
}>`
  ${(props) =>
    props.mode === "campaign" &&
    css`
      position: sticky;
      width: calc(100% + 40px);
      top: 104px;
      margin: 0 -20px;
      padding: 0;
      z-index: 10;
    `}
  > div {
    width: ${(props) => {
      if (["new-email", "edit"].includes(props.mode)) {
        if (props.isFullscreen) {
          return "100%";
        } else {
          return "calc(100vw - 440px)";
        }
      } else if (props.mode === "opened" || props.mode === "campaign") {
        return "100%";
      }
    }};

    position: ${(props) => {
      if (["new-email", "edit"].includes(props.mode)) {
        return "fixed";
      } else if (props.mode === "opened" || props.mode === "campaign") {
        return "absolute";
      }
    }};

    top: ${(props) => {
      if (["new-email", "edit"].includes(props.mode)) {
        if (props.isFullscreen) {
          if (props.isTestDriverUser) {
            return "75px";
          } else {
            return "0px";
          }
        } else {
          if (props.isTestDriverUser) {
            return "162px";
          } else {
            return "87px";
          }
        }
      } else if (props.mode === "opened") {
        return "0px";
      } else if (props.mode === "campaign") {
        return "-21px";
      }
    }};

    left: ${(props) => {
      if (["new-email", "edit"].includes(props.mode)) {
        if (props.isFullscreen) {
          return "0";
        } else {
          return "60px";
        }
      } else if (props.mode === "opened" || props.mode === "campaign") {
        return "0";
      }
    }};

    margin-top: 0px;
    z-index: 1;

    @media screen and (max-width: 1080px) {
      width: 100% !important;
    }

    @media screen and (max-width: 960px) {
      top: ${(props) => {
        if (["new-email", "edit"].includes(props.mode)) {
          if (props.isTestDriverUser && props.isFullscreen) return "75px";
          if (props.isTestDriverUser && !props.isFullscreen) return "228px";
          if (props.isFullscreen) return "0px";
          if (!props.isFullscreen) return "153px";
        }
      }};
    }

    @media screen and (max-width: 650px) {
      top: ${(props) => {
        if (["new-email", "edit"].includes(props.mode)) {
          if (props.isTestDriverUser && props.isFullscreen) return "148px";
          if (props.isTestDriverUser && !props.isFullscreen) return "300px";
          if (props.isFullscreen) return "0px";
          if (!props.isFullscreen) return "150px";
        }
      }};
    }

    @media screen and (max-width: 600px) {
      z-index: ${(props) => {
        if (["new-email", "edit"].includes(props.mode) && props.isFullscreen)
          return "0";
      }};
    }

    @media screen and (max-width: 560px) {
      left: ${(props) => {
        if (["new-email", "edit"].includes(props.mode)) {
          return "0px";
        }
      }};
    }
  }

  > div > div {
    width: 310px;
  }
`;

export interface EditorHandle {
  getEditor: () => Editor | null;
}

export const WIDTH_ZOOM = {
  modified: {
    "300%": "146px",
    "275%": "138px",
    "250%": "130px",
    "225%": "122px",
    "200%": "112px",
    "175%": "104px",
    "150%": "96px",
    "125%": "88px",
    "100%": "80px",
    "75%": "72px",
  },
  default: {
    "300%": "130px",
    "275%": "122px",
    "250%": "114px",
    "225%": "106px",
    "200%": "96px",
    "175%": "88px",
    "150%": "80px",
    "125%": "72px",
    "100%": "64px",
    "75%": "56px",
  },
  fullScreen: {
    "300%": "180px",
    "275%": "176px",
    "250%": "168px",
    "225%": "158px",
    "200%": "149px",
    "175%": "144px",
    "150%": "135px",
    "125%": "130px",
    "100%": "123px",
    "75%": "114px",
  },
};

export const PADDING_ZOOM = {
  default: {
    "300%": "40px 40px !important",
    "275%": "40px 60px !important",
    "250%": "40px 80px !important",
    "225%": "40px 100px !important",
    "200%": "40px 120px !important",
    "175%": "40px 140px !important",
    "150%": "40px 160px !important",
    "125%": "40px 180px !important",
    "100%": "40px 200px !important",
    "75%": "40px 220px !important",
  },
};

export const PADDING_ZOOM_IPAD = {
  default: {
    "300%": "32px 32px !important",
    "275%": "32px 36px !important",
    "250%": "32px 40px !important",
    "225%": "32px 44px !important",
    "200%": "32px 48px !important",
    "175%": "32px 52px !important",
    "150%": "32px 56px !important",
    "125%": "32px 60px !important",
    "100%": "32px 64px !important",
    "75%": "32px 68px !important",
  },
};

export const PADDING_ZOOM_MOBILE = {
  default: {
    "75%": "24px 24px !important",
    "100%": "24px 25px !important",
    "125%": "24px 26px !important",
    "150%": "24px 27px !important",
    "175%": "24px 28px !important",
    "200%": "24px 29px !important",
    "225%": "24px 30px !important",
    "250%": "24px 31px !important",
    "275%": "24px 32px !important",
    "300%": "24px 33px !important",
  },
};

export const DashStyle = {
  default: {
    "300%": "-20px 0px 0px -52px",
    "275%": "-20px 0px 0px -50px",
    "250%": "-18px 0px 0px -45px",
    "225%": "-17px 0px 0px -45px",
    "200%": "-15px 0px 0px -37px",
    "175%": "-13px 0px 0px -35px",
    "150%": "-11px 0px 0px -30px",
    "125%": "-10px 0px 0px -25px",
    "100%": "-6px 0px 0px -18px",
    "75%": "-6px 0px 0px -18px",
  },
};

function updateFontFamilyFromHTML(htmlString, font) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  function removeFontFamily(element) {
    if (element?.nodeType) {
      if (element?.style && element?.style?.fontFamily)
        element.style.fontFamily = font || "";

      const children = element?.childNodes;
      for (let i = 0; i < children.length; i++) {
        removeFontFamily(children[i]);
      }
    }
  }

  removeFontFamily(doc.body);

  return doc.body.innerHTML;
}

interface EmailEditorProps {
  disabled?: boolean;
  mode: EmailMode;
  index?: number;
  campaignLength?: number;
}

export enum TitleType {
  AccountAge = "AccountAge",
  EmailDomain = "EmailDomain",
  SmartSplitUsingBoth = "SmartSplitUsingBoth",
}

const EmailEditor: React.ForwardRefRenderFunction<
  EditorHandle,
  EmailEditorProps
> = ({ disabled, mode, index, campaignLength }, editorRef) => {
  const [email, setEmail] = useRecoilState(
    EmailSelectorFamily({ mode, index })
  );
  const setCharacter = useSetRecoilState(CharactersLenghtAtom);
  const setWords = useSetRecoilState(WordsLengthAtom);
  const fullScreen = useRecoilValue(FullScreenAtom);
  const [zoom, setZoom] = useRecoilState(ZoomAtom);
  const [zoomBeforeFullScreen, setZoomBeforeFullScreen] = useState(zoom);
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const user = useRecoilValue(UserAtom);
  const [isLoading, setIsLoading] = useState<boolean>(mode === "new-email");
  const { isWebKit } = useBrowserAgent();
  const currentEditorRef = useRef<Editor | null>(null);
  const showTestDrive = useRecoilValue(TestDriveBannerAtom);
  const internalUid = localStorageGetItem("INTERNAL_USER_UID");
  const { resetEmailContent } = useSetEmail(
    mode,
    currentEditorRef.current,
    index
  );

  // As it`s used inside the editor callback function, it`s necessary to wrap it inside a ref.
  const links = useRecoilValue(LinksAtom);
  const linksRef =
    useRef<(url: string, from: number, to: number, marks: string[]) => any>();

  const currentLinkQuery = useRef("");
  const currentLinkCommandFunc = useRef<Function | null>(null);
  const colorLinkRef = useRef<string>(
    user?.emailOptions?.defaultModeColorLink || "#000000"
  );
  const boldLinkRef = useRef<boolean>(user?.emailOptions.autoBoldLinks);

  // Link color behavior
  const changeToBoldLinkInEditorRef = useRef(null);
  const changeColorLinkInEditorRef = useRef(null);
  const isLargerThan600 = useMediaQuery("(min-width: 600px)");
  const isLargerThan1380 = useMediaQuery("(min-width: 1380px)");
  const setFullScreen = useSetRecoilState(FullScreenAtom);

  const { subject, splitSubject, setSubjectCallback, setSplitSubjectCallback } =
    useEmailEditorSubject(mode, index);

  function changeColorLinkInEditor() {
    colorLinkRef.current = user?.emailOptions.defaultModeColorLink;
    const autoLinkExtension =
      currentEditorRef.current?.extensionManager.extensions.find(
        ({ name }) => name === "mention"
      )?.options.HTMLAttributes;
    if (autoLinkExtension) {
      autoLinkExtension.colorLink = colorLinkRef.current;
      currentEditorRef.current.commands.setContent(
        currentEditorRef.current.getHTML()
      );
    }
  }

  function changeToBoldLinkInEditor() {
    boldLinkRef.current = user?.emailOptions.autoBoldLinks;
    const autoLinkExtension =
      currentEditorRef.current?.extensionManager.extensions.find(
        ({ name }) => name === "mention"
      )?.options.HTMLAttributes;
    if (autoLinkExtension) {
      autoLinkExtension.autoBoldLinks = boldLinkRef.current;
      if (autoLinkExtension.autoBoldLinks) {
        currentEditorRef.current.commands.setContent(
          currentEditorRef.current
            .getHTML()
            .replaceAll("<a", "<strong><a")
            .replaceAll("</a>", "</a></strong>"),
          true
        );
      } else {
        currentEditorRef.current.commands.setContent(
          currentEditorRef.current
            .getHTML()
            // in case links are just bolded
            .replaceAll(
              `<strong style="font-size: inherit;"><u style="font-size: inherit;"><a`,
              `<u style="font-size: inherit;"><a`
            )
            .replaceAll("</a></u></strong>", "</a></u>")
            .replaceAll(
              `<strong style="font-size: inherit;"><em style="font-size: inherit;"><u style="font-size: inherit;"><a`,
              `<em style="font-size: inherit;"><u style="font-size: inherit;"><a`
            )
            .replaceAll("</a></u></strong>", "</a></u></em>"),
          true
        );
      }
    }
  }

  changeToBoldLinkInEditorRef.current = changeToBoldLinkInEditor;
  changeColorLinkInEditorRef.current = changeColorLinkInEditor;

  // Link tooltips behavior
  const tooltipInstances = useRef<{ [href: string]: Instance<Props> }>({});
  const tiptapMenuHandlerRef = useRef<TiptapMenuHandle>(null);
  const tiptapMenu = tiptapMenuHandlerRef.current?.getTiptapMenu();

  useEffect(() => {
    if (currentEditorRef.current)
      currentEditorRef.current.commands.setContent(
        sanitizeHTML(email.body, sanitizeHtmlOptions)
      );
  }, [index, campaignLength]);

  useLinkTooltips(
    currentEditorRef.current,
    tooltipInstances,
    tiptapMenu,
    isLoading,
    colorLinkRef.current,
    mode,
    index
  );

  useEffect(() => {
    if (changeColorLinkInEditorRef.current)
      changeColorLinkInEditorRef.current();
  }, [user?.emailOptions.defaultModeColorLink]);

  useEffect(() => {
    if (changeToBoldLinkInEditorRef.current)
      changeToBoldLinkInEditorRef.current();
  }, [user?.emailOptions.autoBoldLinks]);

  useEffect(() => {
    let fixCursorOnFrontOfTheLinkHandler = fixCursorOnFrontOfTheLink();

    return () => {
      fixCursorOnFrontOfTheLinkHandler.clearListener();
    };
  });

  // Link and draft handling
  useEffect(() => {
    linksRef.current = (
      url: string,
      from: number,
      to: number,
      marks: string[]
    ) => {
      const interestTag = links.find((link) => link.url === url);

      if (url) {
        let queriedURL = "";
        let positionCounter = 0;

        do {
          queriedURL = queryString.stringifyUrl({
            url: interestTag ? interestTag.url : url,
            query: { __position__: positionCounter },
          });

          positionCounter = positionCounter + 1;
        } while (
          //@ts-ignore
          Object.keys(email?.interestingURLs || {}).includes(queriedURL)
        );
        currentEditorRef.current.commands.addURLInRange({
          marks,
          from,
          to,
          id: queriedURL,
          label: url,
          colorLink: user?.emailOptions.defaultModeColorLink,
          autoBoldLinks: !!user?.emailOptions.autoBoldLinks,
          tooltipId: nanoid(),
        });

        setEmail((editedEmail) => ({
          ...editedEmail,
          interestingURLs: {
            //@ts-ignore
            ...editedEmail.interestingURLs,
            [queriedURL]: [],
          },
        }));
      }
    };
  }, [
    links,
    email,
    user?.emailOptions.defaultModeColorLink,
    user?.emailOptions.autoBoldLinks,
    user,
    setEmail,
  ]);

  // GetUsetTheme
  useEffect(() => {
    const colors = user?.emailOptions.theme || themes[0];

    if (colors)
      setEmail((oldActiveEmail) => {
        return {
          ...oldActiveEmail,
          fontColor: colors.textColor,
          backgroundColor: colors.color,
          placeholderColor: colors.placeholderColor,
          titleColor: colors.titleColor,
          titlePlaceholderColor: colors.titlePlaceholderColor,
        };
      });
  }, [user, setEmail]);

  const previousFullScreen = usePrevious(fullScreen);
  useEffect(() => {
    if (previousFullScreen === fullScreen) return;
    if (fullScreen) {
      setZoomBeforeFullScreen(zoom);
    } else {
      setZoom(zoomBeforeFullScreen);
    }
  }, [fullScreen, previousFullScreen, zoom, zoomBeforeFullScreen]);

  const sharedHistoryRef = useEmailEditorHistory();

  const { handleCtrlY, handleCtrlZ } = useEditorHistory(
    sharedHistoryRef,
    currentEditorRef.current
  );

  // Pasting behavior
  const {
    checkEmailBodyAsFirstLineRef,
    fastBackspaceRef,
    onPasteFromTextArea,
  } = useEmailEditorPasting(
    currentEditorRef.current,
    // setEmailSubject,
    // setSplitSubject,
    sharedHistoryRef,
    mode,
    index
  );

  const { updateMenuAtom } = useUpdateBubbleMenu();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        code: false,
        codeBlock: false,
        heading: false,
        bold: {
          HTMLAttributes: {
            style: "font-size: inherit;",
          },
        },
        italic: {
          HTMLAttributes: {
            style: "font-size: inherit;",
          },
        },
        listItem: {
          HTMLAttributes: {
            style: "margin: 15px 0px;",
          },
        },
        horizontalRule: false,
        history: false,
      }),

      Placeholder.configure({
        placeholder: "Enter content…",
      }),
      HorizontalRule,
      Underline.configure({
        HTMLAttributes: {
          style: "font-size: inherit;",
          color: colorLinkRef.current,
        },
      }),
      Strike.configure({
        HTMLAttributes: { style: "font-size: inherit;" },
      }),
      TiptapHeading.configure({
        levels: [1, 2, 3],
        HTMLAttributes: {
          style: "line-height: 1.4; margin: 0px;",
        },
      }),
      // DashedList.configure({
      //   itemTypeName: "dashedItem",
      //   HTMLAttributes: {},
      // }),
      // DashedItem.configure({
      //   nested: true,
      //   HTMLAttributes: {},
      // }),
      Document.extend({
        // Overwrite the sortcuts so they are recognized on the Floating Menu, yes, I know...
        addKeyboardShortcuts() {
          return {
            "Mod-b": () => {
              if (mode === "send-to-contact") return true;
              const selectedAnchor: HTMLAnchorElement | null =
                getSelectedAnchorElement();

              if (selectedAnchor) {
                const boldByStyle =
                  selectedAnchor.style.fontWeight &&
                  /^(bold(er)?|[5-9]\d{2,})$/.test(
                    selectedAnchor.style.fontWeight as string
                  );

                let boldByDecoration: any = selectedAnchor.parentNode;
                let isBoldByDecoration = boldByDecoration.nodeName === "STRONG"; // replace with direct parent

                if (!isBoldByDecoration) {
                  boldByDecoration = boldByDecoration.parentNode;
                  isBoldByDecoration = boldByDecoration.nodeName === "STRONG"; // replace with direct parent
                }

                if (!isBoldByDecoration) {
                  boldByDecoration = boldByDecoration.parentNode;
                  isBoldByDecoration = boldByDecoration.nodeName === "STRONG"; // replace with direct parent
                }

                if (isBoldByDecoration && boldByStyle) {
                  selectedAnchor.style.fontWeight = "none !important";
                  this.editor.chain().focus().unsetBold().run();
                } else if (isBoldByDecoration) {
                  this.editor.chain().focus().unsetBold().run();
                } else if (boldByStyle) {
                  selectedAnchor.style.fontWeight = "none !important";
                  this.editor.chain().focus().unsetBold().run();
                } else {
                  this.editor.chain().focus().toggleBold().run();
                }
              } else {
                this.editor
                  .chain()
                  .focus()
                  .selectNodeBackward()
                  .toggleBold()
                  .run();
              }
              return true;
            },
            "Mod-u": () => {
              if (mode === "send-to-contact") return true;
              const selectedAnchor: HTMLAnchorElement | null =
                getSelectedAnchorElement();

              if (selectedAnchor) {
                const underlinedByStyle =
                  selectedAnchor.style.textDecoration === "underline";

                let underlinedByDecoration: any = selectedAnchor.parentNode;
                let isUnderlinedByDecoration =
                  underlinedByDecoration.nodeName === "U"; // replace with direct parent

                if (isUnderlinedByDecoration && underlinedByStyle) {
                  selectedAnchor.style.textDecoration = "none !important";
                  this.editor.chain().focus().unsetUnderline().run();
                } else if (isUnderlinedByDecoration) {
                  this.editor.chain().focus().unsetUnderline().run();
                } else if (underlinedByStyle) {
                  selectedAnchor.style.textDecoration = "none !important";
                  this.editor.chain().focus().unsetUnderline().run();
                } else {
                  this.editor.chain().focus().toggleUnderline().run();
                }
              } else {
                this.editor.chain().focus().toggleUnderline().run();
              }
              return true;
            },
            "Mod-i": () => {
              if (mode === "send-to-contact") return true;
              const selectedAnchor: HTMLAnchorElement | null =
                getSelectedAnchorElement();

              if (selectedAnchor) {
                const italicByStyle =
                  selectedAnchor.style.fontStyle === "italic";

                let italicByDecoration: any = selectedAnchor.parentNode;
                let isItalicByDecoration = italicByDecoration.nodeName === "EM"; // replace with direct parent

                if (!isItalicByDecoration) {
                  italicByDecoration = italicByDecoration.parentNode;
                  isItalicByDecoration = italicByDecoration.nodeName === "EM"; // replace with direct parent
                }

                if (isItalicByDecoration && italicByStyle) {
                  selectedAnchor.style.fontStyle = "none !important";
                  this.editor.chain().focus().unsetItalic().run();
                } else if (isItalicByDecoration) {
                  this.editor.chain().focus().unsetItalic().run();
                } else if (italicByStyle) {
                  selectedAnchor.style.fontStyle = "none !important";
                  this.editor.chain().focus().unsetItalic().run();
                } else {
                  // Special custom styles treatment
                  // if (CustomStylingUtils.isCustomStyleActive(this.editor)) {
                  //   return CustomStylingUtils.toggleCustomStyleMark(
                  //     this.editor
                  //   );
                  // }

                  this.editor.chain().focus().toggleItalic().run();
                }
              } else {
                // Special custom styles treatment
                // if (CustomStylingUtils.isCustomStyleActive(this.editor)) {
                //   return CustomStylingUtils.toggleCustomStyleMark(this.editor);
                // }

                this.editor.chain().focus().toggleItalic().run();
              }
              return true;
            },
            "Mod-Alt-1": () => {
              if (mode === "send-to-contact") return true;
              updateMenuAtom();

              let chain = this.editor.chain().toggleHeading({ level: 1 });
              editorChainKeepAlign(this.editor, chain);
              return chain.run();
            },
            "Mod-Alt-2": () => {
              if (mode === "send-to-contact") return true;
              updateMenuAtom();

              let chain = this.editor.chain().toggleHeading({ level: 2 });
              editorChainKeepAlign(this.editor, chain);
              return chain.run();
            },
            "Mod-Alt-3": () => {
              if (mode === "send-to-contact") return true;
              updateMenuAtom();

              let chain = this.editor.chain().toggleHeading({ level: 3 });
              editorChainKeepAlign(this.editor, chain);
              return chain.run();
            },
            "Mod-Alt-L": () => {
              if (mode === "send-to-contact") return true;
              updateMenuAtom();
              return this.editor.commands.setTextAlign("left");
            },
            "Mod-Alt-E": () => {
              if (mode === "send-to-contact") return true;
              updateMenuAtom();
              return this.editor.commands.setTextAlign("center");
            },
            "Mod-Alt-R": () => {
              if (mode === "send-to-contact") return true;
              updateMenuAtom();
              return this.editor.commands.setTextAlign("right");
            },
            "Mod-Alt-J": () => {
              if (mode === "send-to-contact") return true;
              updateMenuAtom();
              return this.editor.commands.setTextAlign("justify");
            },
          };
        },
        onSelectionUpdate() {
          updateMenuAtom();
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Paragraph.configure({
        HTMLAttributes: {
          style: "margin: 0px",
        },
      }),
      TiptapAutoLink.configure({
        HTMLAttributes: {
          class: "mention",
          colorLink: colorLinkRef.current,
          autoBoldLinks: boldLinkRef.current,
        },
        suggestion: {
          items: (query) => {
            return [];
          },
          render: () => {
            return {
              onStart: (props) => {
                currentLinkQuery.current = props.query;
                currentLinkCommandFunc.current = props.command;
              },
              onUpdate(props) {
                currentLinkQuery.current = props.query;
                currentLinkCommandFunc.current = props.command;
              },
              onExit({ command, query, text, range, marks }) {
                let positionMovingTo =
                  currentEditorRef.current.state.selection.$anchor;

                const { from, to } = range;

                let node = null;
                try {
                  node = currentEditorRef.current.state.doc.nodeAt(from);
                } catch {}

                const nodeText = node?.textContent;
                // It's being fired sometimes, so I'm checking it before continuing
                if (!nodeText) return;
                const nodePosition = nodeText.indexOf(text) + text.length;
                const substringText = nodeText.substring(nodePosition);
                const words = substringText.split(/[\s,]+/);
                const firstOccurrence = words[0];

                const newFrom = to - text.length;
                const newTo = to + firstOccurrence.length;

                // Move the cursor to the next empty space, taking the cursor to the
                // end of the word (https://goo|gle.com => https://google.com|)
                if (command) {
                  let url = `h${query}${firstOccurrence}`;

                  if (!isURL(url)) return;

                  // This changes the position, so I need to revert back
                  // to normal. Also, it's wrapped in a setTimeout to avoid
                  // racing with the tiptap core keyboard listener. It's a temporaty
                  // solution though, we can make it more sophisticated by introducing
                  // two event callbacks on the suggestions plugin!
                  setTimeout(() => {
                    linksRef.current(url, newFrom, newTo, marks);

                    let positionImAt =
                      currentEditorRef.current.state.selection.$anchor;

                    //If you put the cursor after the mention, we need to remove url.length on position
                    if (positionMovingTo.pos > positionImAt.pos) {
                      currentEditorRef.current.commands.focus(
                        positionMovingTo.pos - url.length + 1
                      );
                    } else {
                      currentEditorRef.current.commands.focus(
                        positionMovingTo.pos
                      );
                    }
                  }, 50);
                }
              },
            };
          },
        },
      }),
      Text,
      FontFamily,
      BulletList,
      TextStyle,
      CustomStylingTiptap,
      History.extend({
        // @ts-ignore
        addKeyboardShortcuts() {
          return {
            "Mod-z": () => handleCtrlZ(),
            "Mod-y": () => handleCtrlY(),
            "Shift-Mod-z": () => handleCtrlY(),

            // Russian keyboard layouts
            "Mod-я": () => handleCtrlZ(),
            "Shift-Mod-я": () => handleCtrlY(),
          };
        },
      }),
    ],
    content: sanitizeHTML(email.body, sanitizeHtmlOptions),

    editorProps: {
      transformPastedHTML: (html) => {
        let htmlWithPagesMargins;
        if (BrowserAgent.isWebKit()) {
          htmlWithPagesMargins = convertPagesMarginsToLineBreaksOnWebkit(html);
        } else {
          htmlWithPagesMargins = convertPagesMarginsToLineBreaksOnChrome(html);
        }

        let result = juice(htmlWithPagesMargins || html);

        let parsed = sanitizeHTML(
          result,
          mode === "send-to-contact"
            ? sentToOneSanitizeHtmlOptions
            : sanitizeHtmlOptions
        );

        // Add bold to links if needed
        if (boldLinkRef.current) {
          parsed = addBoldInLinks(parsed);
        }
        if (isSafari) {
          parsed = parsed.replaceAll(
            "</a></span></p>",
            "</a></span><span>‎ </span></p>"
          );
        }

        parsed = updateFontFamilyFromHTML(
          parsed,
          user?.emailOptions.font.family
        );

        return parsed;
      },
      transformPastedText: (text) => {
        let formattedText = text.trim();
        formattedText = sanitizeHTML(
          formattedText,
          mode === "send-to-contact"
            ? sentToOneSanitizeHtmlOptions
            : sanitizeHtmlOptions
        );
        formattedText = HTMLEntities.decode(formattedText);
        console.log("formattedText", formattedText);
        return formattedText;
      },
      clipboardTextParser: (text, context) => {
        text = text.replaceAll("\r\n", "\n");
        text = text.replaceAll(/^( |\t|\f)*\r?\n/gm, REGEX_SEPARATOR);
        text = text.replaceAll(`\n${REGEX_SEPARATOR}`, REGEX_SEPARATOR);
        let blocks = text.split(REGEX_SEPARATOR);
        blocks = blocks
          .map((b, index) =>
            index < blocks.length - 1 && b.length ? [b, REGEX_SEPARATOR] : b
          )
          .flat();
        blocks = blocks.map((b) => b.split("\n")).flat();

        const nodes = [];
        const {
          state,
          state: { schema },
        } = currentEditorRef.current;

        let marks = [];
        const collapsed = window.getSelection().isCollapsed;
        if (collapsed) {
          marks = selectionMarks(state, schema);
        }

        blocks.forEach((line) => {
          if (line === REGEX_SEPARATOR) {
            const extraLineNode = Node.fromJSON(context.doc.type.schema, {
              type: "paragraph",
            });
            nodes.push(extraLineNode);
          } else {
            let nodeJson: { [key: string]: any } = { type: "paragraph" };
            if (line.length > 0) {
              nodeJson.content = linkify(
                line,
                colorLinkRef.current || "#000000",

                // Check if I have "autoBoldLink" enabled, if so, pass a mark to always be added.
                // In this case, a "bold" mark.
                boldLinkRef.current ? [...marks, "bold"] : marks,
                boldLinkRef.current
              );
            }
            const node = Node.fromJSON(context.doc.type.schema, nodeJson);
            nodes.push(node);
          }
        });

        const fragment = Fragment.fromArray(nodes);
        return Slice.maxOpen(fragment);
      },
      transformPasted: (slice: Slice) => {
        slice.content.descendants((node) => {
          if (
            node.type.name === "mention" &&
            isNotTrimmedURL(node.attrs.label || "")
          )
            node.marks = [
              ...node.marks,
              currentEditorRef.current.schema.marks["underline"].create({
                color: colorLinkRef.current,
              }),
              ...(user.emailOptions.autoBoldLinks
                ? [currentEditorRef.current.schema.marks["bold"].create()]
                : []),
            ];
        });

        return slice;
      },
      handlePaste: (view, e: ClipboardEvent, slice) => {
        if (fastBackspaceRef.current) {
          fastBackspaceRef.current = false;
          return false;
        }

        checkEmailBodyAsFirstLineRef.current(view, e, slice);
        return true;
      },
    },
    onUpdate: ({ editor }) => {
      if (setEmailFunc.current) setEmailFunc.current();

      if (
        (internalUid === INTERNAL_USER_FOR_AUTO_TEXT_STYLE &&
          mode === "new-email") ||
        (mode === "new-email" && window.location.host.includes("staging")) ||
        (mode === "new-email" && window.location.host.includes("localhost"))
          ? true
          : false
      ) {
        // CustomStylingUtils.onEditorUpdate(
        //   editor,
        //   isExecutingComposition.current
        // );
      }
    },
    parseOptions: {
      preserveWhitespace: "full",
    },
  });

  // const { isExecutingComposition } =
  //   CustomStylingUtils.useIsExecutingComposition({
  //     editor,
  //     enabled:
  //       (internalUid === INTERNAL_USER_FOR_AUTO_TEXT_STYLE &&
  //         mode === "new-email") ||
  //       (mode === "new-email" && window.location.host.includes("staging")) ||
  //       (mode === "new-email" && window.location.host.includes("localhost"))
  //         ? true
  //         : false,
  //   });

  const setEmailFunc = useRef<() => void | null>(null);

  setEmailFunc.current = useCallback(
    debounce(() => {
      if (!editor) return;

      const editorHTML = editor.getHTML();

      setEmail((e) => ({ ...e, body: editorHTML }));

      if (mode === "new-email") {
        localStorageSetItem("DRAFT_EMAIL_BODY", editorHTML);
      }

      const hasLink = editor.view.dom.innerHTML.includes("<a");

      if (editor.state.doc.textContent.trim().length === 0 && !hasLink) {
        setWords(0);

        //When the editor is empty, delete draft email from localstorage
        localStorageRemoveItem("DRAFT_EMAIL_BODY");
        localStorageRemoveItem("DRAFT_INTEREST_TAGS");
      } else {
        let cont = editorHTML.replace(/<[^>]*>/g, " ");
        cont = cont.replace(/\s+/g, " ");
        cont = cont.trim();
        const numberOfWords = cont
          .split("&nbsp;")
          .join("")
          .replace(/\s\s+/g, " ")
          .trim()
          .split(" ").length;
        const numberOfCharacters = cont
          .split("&nbsp;")
          .join("")
          .trim()
          .split(" ")
          .join("").length;

        setWords(numberOfWords);
        setCharacter(numberOfCharacters);
      }
    }, 200),
    [editor, setEmail]
  );

  useEffect(() => {
    if (!editor) return null;

    currentEditorRef.current = editor;

    if (disabled) {
      editor.setEditable(false);
    }
  }, [editor]);

  useImperativeHandle(
    editorRef,
    () => ({
      getEditor: () => editor,
    }),
    [editor]
  );

  useEffect(() => {
    if (!editor) return null;
    if (mode === "edit") {
      changeColorLinkInEditorRef.current();
      changeToBoldLinkInEditorRef.current();
      setIsLoading(false);
      return;
    }
    if (mode !== "new-email") return;
    if (process.browser) {
      // Check if it's a duplicate
      const urlParams = new URL(window.location as any).searchParams;

      if (urlParams.get("duplicate") === "true") {
        // Done to propagate in the case I come in via duplicate email
        localStorageSetItem("DRAFT_EMAIL_BODY", email.body);
        localStorageSetItem("DRAFT_EMAIL_SUBJECT", email.subject);
        localStorageSetItem("DRAFT_EMAIL_LABELS", JSON.stringify(email.labels));

        changeColorLinkInEditorRef.current();
        changeToBoldLinkInEditorRef.current();

        setIsLoading(false);
        return;
      }
    }

    const draftEmailBody = localStorageGetItem("DRAFT_EMAIL_BODY") as string;

    const encodedDraftEmailInterestTags = localStorageGetItem(
      "DRAFT_INTEREST_TAGS"
    ) as string;
    let draftEmailInterestTags;
    if (encodedDraftEmailInterestTags) {
      draftEmailInterestTags = JSON.parse(encodedDraftEmailInterestTags);
    }

    const draftEmailSubject = localStorageGetItem(
      "DRAFT_EMAIL_SUBJECT"
    ) as string;

    const draftEmailLabelsString = localStorageGetItem(
      "DRAFT_EMAIL_LABELS"
    ) as string;
    let draftEmailLabels: string[] = [];
    if (draftEmailLabelsString)
      draftEmailLabels = JSON.parse(draftEmailLabelsString);

    const draftEmailSplitSubject = localStorageGetItem(
      "DRAFT_EMAIL_SPLIT_SUBJECT"
    ) as string;
    const draftEmailSplitMode = localStorageGetItem(
      "DRAFT_EMAIL_SPLIT_MODE"
    ) as string;

    setEmail((prevNewEmail) => {
      return {
        ...prevNewEmail,
        ...(draftEmailBody ? { body: draftEmailBody } : { body: "" }),
        ...(draftEmailSubject
          ? { subject: draftEmailSubject }
          : { subject: "" }),
        ...(draftEmailSplitSubject
          ? { splitSubject: draftEmailSplitSubject }
          : { splitSubject: "" }),
        ...(draftEmailInterestTags && {
          interestingURLs: draftEmailInterestTags,
        }),
        ...(draftEmailSplitMode
          ? {
              splitMode: draftEmailSplitMode as "AGE" | "DOMAIN" | "AGE_DOMAIN",
            }
          : { splitMode: undefined }),
        subject: draftEmailSubject || "",
        splitSubject: draftEmailSplitSubject || "",
        ...(draftEmailLabels &&
          draftEmailLabels.length > 0 && { labels: draftEmailLabels }),
      };
    });

    if (draftEmailBody) {
      editor.commands.setContent(draftEmailBody, true);
      setCharacter(editor.getCharacterCount());
      if (editor.state.doc.textContent.trim().length === 0) {
        setWords(0);
      } else {
        let cont = editor.getHTML().replace(/<[^>]*>/g, " ");
        cont = cont.replace(/\s+/g, " ");
        cont = cont.trim();
        const numberOfWords = cont
          .split("&nbsp;")
          .join("")
          .replace(/\s\s+/g, " ")
          .trim()
          .split(" ").length;
        const numberOfCharacters = cont
          .split("&nbsp;")
          .join("")
          .trim()
          .split(" ")
          .join("").length;

        setWords(numberOfWords);
        setCharacter(numberOfCharacters);
      }
    }

    setIsLoading(false);
  }, [editor, mode, setEmail]);

  useEffect(() => {
    if (email.interestingURLs) {
      localStorageSetItem(
        "DRAFT_INTEREST_TAGS",
        JSON.stringify(email.interestingURLs)
      );
    }
  }, [email.interestingURLs]);

  // I've included this useEffect as a workaround to the problem
  // where the emailBody(from editMode) was included in the
  // NewEmail's body. So, I'm clearing this up on the unmount.
  // Obs: doesn't affect the draft_email_content in the LocalStorage
  // (where I write something in the NewEmail -> go to another page -> come back
  // and the content is still there)
  useEffect(() => {
    if (mode !== "edit") return;
    resetEmailContent.current();
  }, [mode]);

  // useEffect(() => {
  //   setTimeout(() => {
  //     let item = document.querySelector(".ProseMirrorBody");

  //     if (item) {
  //       item.addEventListener("copy", function (e) {
  //         let newHtml = e.target as any;

  //         newHtml.classList.add("fromBM");

  //         (e as any).clipboardData.setData(
  //           "text/html",
  //           newHtml.parentElement.innerHTML
  //         );

  //         e.preventDefault();
  //       });
  //     }
  //   }, 100);
  // }, [editor]);

  if (!user || isLoading) return null;

  const zoomedRate =
    parseInt(mode !== "campaign" ? zoom.name.slice(0, -1) : "100", 10) / 100;
  const zoomedWidth = user?.emailOptions.maxWidth * zoomedRate;
  const zoomedSubjectFontSize = 24 * zoomedRate;
  const zoomedBodyFontSize = (user.emailOptions.font.size || 14) * zoomedRate;

  const defaultSubjectStyle = {
    fontWeight: "bold",
    lineHeight: "1.2em",
    border: "none",
    borderRadius: "0px",
    // HERE!
    // @ts-ignore
    backgroundColor: `${mode === "campaign" ? "#ffff" : email.backgroundColor}`,
    // HERE!
    // @ts-ignore
    color: `${mode === "campaign" ? "#000" : email.fontColor}`,
  };
  const defaultWrapperWidth = user?.emailOptions.maxWidth
    ? `${zoomedWidth}px`
    : "100%";

  const isTestDriverUser = showTestDrive.show;

  return (
    <EmailBody
      className={"ProseMirrorBody"}
      isResend={mode === "opened"}
      isCampaign={mode === "campaign"}
      fullScreen={fullScreen}
      style={
        mode !== "campaign"
          ? {
              // HERE!
              // @ts-ignore
              backgroundColor: email.backgroundColor,
              // HERE!
              // @ts-ignore
              color: email.fontColor,
            }
          : {
              backgroundColor: "#ffff",
            }
      }
      css={css`
        overflow: ${["campaign"].includes(mode) && "unset !important"};
        padding: ${["campaign", "send-to-contact"].includes(mode)
          ? "40px"
          : isLargerThan600 && !isLargerThan1380
          ? PADDING_ZOOM_IPAD["default"][zoom.name]
          : !isLargerThan600
          ? PADDING_ZOOM_MOBILE["default"][zoom.name]
          : PADDING_ZOOM["default"][zoom.name]};

        ${mode === "send-to-contact" &&
        css`
          padding-top: 0px;
        `}

        .close-button {
          width: 20px;
          height: 20px;

          display: block;
          position: absolute;
          top: 16px;
          right: 16px;

          svg {
            width: 100%;
            height: 100%;
          }
        }
        position: relative;
      `}
    >
      {mode !== "send-to-contact" && editor && (
        <EditorToolbarWrapper
          mode={mode}
          isTestDriverUser={isTestDriverUser}
          isFullscreen={fullScreen}
        >
          <EditorToolbar
            editor={editor}
            tooltips={tooltipInstances}
            mode={mode}
            index={index}
          />
        </EditorToolbarWrapper>
      )}

      {!isLargerThan600 && fullScreen && (
        <span
          onClick={() => setFullScreen(!fullScreen)}
          className="close-button"
        >
          <IoMdClose />
        </span>
      )}
      {/* I'm hiding the TiptapMenu because of the new Toolbar implementation. So, if Troy wants it back
        someday, we just need to change from "false" to "editor" in this AND below. */}
      {false && (
        <TiptapMenu
          editor={editor}
          isCampaign={mode === "campaign"}
          ref={tiptapMenuHandlerRef}
          tooltips={tooltipInstances}
          mode={mode}
          index={index}
        />
      )}
      <EmailWrapper
        css={css`
          margin: 40px auto 0 0;
          padding: ${user.emailOptions.splitTesting && fullScreen && "40px"};
          align-items: ${user.emailOptions.splitTesting && mode !== "edit"
            ? "flex-end"
            : "flex-start"};
          width: ${user.emailOptions.splitTesting
            ? user?.emailOptions.maxWidth
              ? "unset"
              : "100%"
            : "100%"};

          @media screen and (max-width: 1024px) {
            /* padding-top: 96px; */

            div > textarea {
              padding: ${fullScreen && "0 !important"};
            }
          }
        `}
      >
        {editor && (
          <>
            <Flex
              css={css`
                min-width: 100%;
              `}
              align="center"
              justify="start"
              gridGap={
                mode === "new-email" && user.emailOptions.splitTesting
                  ? "16px"
                  : "unset"
              }
            >
              {mode === "new-email" && user.emailOptions.splitTesting && (
                <SplitModePopover
                  zoom={zoom}
                  // HERE!
                  // @ts-ignore
                  newEmailSplitMode={email.splitMode}
                  setNewEmail={setEmail}
                />
              )}

              {
                // HERE!
                // @ts-ignore
                !email.splitMode || !user.emailOptions.splitTesting ? (
                  <ResizebleTextArea
                    sharedHistoryRef={sharedHistoryRef}
                    disabled={disabled}
                    value={subject}
                    onChange={(evt: any) => {
                      setSubjectCallback(evt.target.value);
                    }}
                    onPaste={(e) => {
                      onPasteFromTextArea(e, SUBJECT_KEY);
                    }}
                    placeholder="Enter subject"
                    id={SUBJECT_KEY}
                    // @ts-ignore
                    style={{
                      maxWidth: `${defaultWrapperWidth}`,
                      width: `${defaultWrapperWidth}`,
                      fontSize: `${zoomedSubjectFontSize}px`,
                      padding: "5px 0px",
                      overflowY: "hidden",
                      ...defaultSubjectStyle,
                    }}
                  />
                ) : (
                  <Flex
                    direction="column"
                    css={css`
                      max-width: ${defaultWrapperWidth};
                      width: ${defaultWrapperWidth};

                      #split_subject {
                        &::placeholder {
                          color: #959a9d !important;
                        }
                      }
                    `}
                  >
                    <ResizebleTextArea
                      sharedHistoryRef={sharedHistoryRef}
                      disabled={disabled}
                      value={subject}
                      onChange={(evt: any) => {
                        setSubjectCallback(evt.target.value);
                      }}
                      onPaste={(e) => onPasteFromTextArea(e, SUBJECT_KEY)}
                      placeholder="Enter subject"
                      id={SUBJECT_KEY}
                      // @ts-ignore
                      style={{
                        width: "100%",
                        fontSize: `${zoomedSubjectFontSize}px`,
                        padding: "5px 0px",
                        overflowY: "hidden",
                        ...defaultSubjectStyle,
                      }}
                    />
                    <ResizebleTextArea
                      sharedHistoryRef={sharedHistoryRef}
                      disabled={disabled}
                      // HERE!
                      // @ts-ignore
                      value={splitSubject}
                      onChange={(evt: any) => {
                        setSplitSubjectCallback(evt.target.value);
                      }}
                      onPaste={(e) => onPasteFromTextArea(e, SPLIT_SUBJECT_KEY)}
                      placeholder="Enter subject"
                      id={SPLIT_SUBJECT_KEY}
                      // @ts-ignores
                      style={{
                        width: "100%",
                        fontSize: `${zoomedSubjectFontSize}px`,
                        padding: "5px 0px",
                        overflowY: "hidden",
                        ...defaultSubjectStyle,
                      }}
                    />
                  </Flex>
                )
              }
            </Flex>

            <Body
              webkit={isWebKit}
              css={css`
                max-width: ${user.emailOptions.splitTesting &&
                mode !== "campaign" &&
                mode !== "opened" &&
                !user.emailOptions.maxWidth
                  ? `calc(100% - (${WIDTH_ZOOM["default"][zoom.name]} + 16px))`
                  : defaultWrapperWidth};
                width: ${user.emailOptions.splitTesting &&
                mode !== "campaign" &&
                mode !== "opened" &&
                !user.emailOptions.maxWidth
                  ? `calc(100% - ${WIDTH_ZOOM["default"][zoom.name]})`
                  : defaultWrapperWidth};

                padding: ${mode === "campaign" ? "5px 0px 240px" : "5px 0px"};

                overflow-y: ${fullScreen ? "auto" : "none"};

                font-size: ${zoomedBodyFontSize}px;

                color: ${mode === "campaign"
                  ? "#2f2f2f"
                  : // HERE!
                    // @ts-ignore
                    email.fontColor};
                letter-spacing: 0.025em;

                flex: 1;

                background-color: ${mode === "campaign"
                  ? "#ffff"
                  : // HERE!
                    // @ts-ignore
                    email.backgroundColor};

                * {
                  font-family: ${user.emailOptions.font.family || "Arial"};
                }

                .is-editor-empty:first-of-type::before {
                  color: ${
                    // HERE!
                    // @ts-ignore
                    email.placeholderColor || "#00000040"
                  } !important;
                }

                hr {
                  border-color: ${user?.emailOptions?.theme?.titleColor ===
                  "#ffffff"
                    ? "#ababab"
                    : "#1a0001a"};
                }
                // This block of code is causing the bullet points to not work on copy paste

                /* ul[data-type="dashedList"] {
                  list-style-type: none;
                  > li {
                    margin: 15px 0px;
                  }
                  > li::before {
                    content: "_";
                    position: absolute;
                    margin: ${DashStyle["default"][zoom.name]};
                  }
                } */
              `}
              onClick={() => {
                editor.commands.focus();
              }}
              ref={editorContainerRef}
              isCampaign={mode === "campaign"}
            >
              <EditorContent editor={editor} />
            </Body>
          </>
        )}
      </EmailWrapper>
    </EmailBody>
  );
};

export default React.forwardRef(EmailEditor);
